<script>
  let {
    years,
    outcome,
    phased = false,
    xTicks,           // optional [n, n, ...] custom tick offsets (into `years`)
    xTickLabel,       // optional (offset: number) => string — default is Y{n}
    width = 640,
    height = 260,
  } = $props();

  const PADDING = { top: 16, right: 12, bottom: 34, left: 60 };

  // Hover state — index into `years` (not `points`, which has an extra
  // year-0 origin at the start). null = no hover.
  let hoveredYearIndex = $state(null);
  let svgEl;

  function fmtGBPFull(n) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);
  }

  function fmtPctFull(n) {
    return new Intl.NumberFormat("en-GB", {
      style: "percent",
      maximumFractionDigits: 2,
    }).format(n);
  }

  function phaseLabel(phase) {
    if (phase === "study") return "Uni year";
    if (phase === "gap") return "Gap year";
    if (phase === "repay") return "Repayment year";
    return null;
  }

  function handlePointer(e) {
    if (!svgEl || years.length === 0) return;
    const rect = svgEl.getBoundingClientRect();
    // Convert screen x → svg viewBox x
    const svgX = ((e.clientX - rect.left) / rect.width) * width;
    // Convert svg x → data x (year value)
    const dataX = xStart + ((svgX - PADDING.left) / plotWidth) * xRange;
    // Nearest year row
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < years.length; i++) {
      const d = Math.abs(years[i].year - dataX);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    hoveredYearIndex = bestIdx;
  }

  function handleLeave() {
    hoveredYearIndex = null;
  }

  const hoveredRow = $derived(
    hoveredYearIndex != null ? years[hoveredYearIndex] : null,
  );

  const hoverX = $derived(hoveredRow ? xScale(hoveredRow.year) : null);
  const hoverY = $derived(hoveredRow ? yScale(hoveredRow.closingBalance) : null);

  // Tooltip position — bounded so it doesn't fall off the right edge.
  const tooltipLeftPct = $derived.by(() => {
    if (hoverX == null) return 0;
    const pct = (hoverX / width) * 100;
    return Math.max(2, Math.min(98, pct));
  });

  const plotWidth = $derived(width - PADDING.left - PADDING.right);
  const plotHeight = $derived(height - PADDING.top - PADDING.bottom);

  // Use the first year's x-position, but each row's `year` may not start at 1
  // (e.g. pre-uni combined data still starts at 1). The chart x-axis maps
  // linearly from year=0 to year=nYears.
  const xStart = $derived(years.length > 0 ? years[0].year - 1 : 0);
  const xEnd = $derived(years.length > 0 ? years[years.length - 1].year : 1);
  const xRange = $derived(xEnd - xStart);

  const maxBalance = $derived(
    years.length > 0
      ? Math.max(...years.map((y) => Math.max(y.openingBalance, y.closingBalance)))
      : 0,
  );

  // Include (xStart, opening balance of first row) as the leftmost point.
  // Each subsequent point is (row.year, row.closingBalance).
  const points = $derived(
    years.length === 0
      ? []
      : [
          { x: xStart, y: years[0].openingBalance, phase: years[0].phase },
          ...years.map((y) => ({ x: y.year, y: y.closingBalance, phase: y.phase })),
        ],
  );

  const xScale = $derived((x) =>
    xRange > 0 ? PADDING.left + ((x - xStart) / xRange) * plotWidth : PADDING.left,
  );
  const yScale = $derived((y) =>
    maxBalance > 0
      ? PADDING.top + (1 - y / maxBalance) * plotHeight
      : PADDING.top + plotHeight,
  );

  // Full single-colour line path — used when phased=false.
  const linePath = $derived(
    points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
      .join(" "),
  );

  // Area fill (single colour, subtle) — always drawn.
  const areaPath = $derived(
    points.length > 0
      ? points
          .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
          .join(" ") +
          ` L ${xScale(points[points.length - 1].x)} ${PADDING.top + plotHeight}` +
          ` L ${xScale(points[0].x)} ${PADDING.top + plotHeight}` +
          " Z"
      : "",
  );

  // For phased mode: group consecutive points by phase, sharing the transition
  // point between adjacent segments so the visual line is continuous.
  const phaseSegments = $derived.by(() => {
    if (!phased || points.length === 0) return [];
    const segments = [];
    let curPhase = points[1]?.phase ?? points[0].phase;
    let curPoints = [points[0]];
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (p.phase !== curPhase) {
        // Close current segment at the transition point
        curPoints.push(p);
        segments.push({ phase: curPhase, points: curPoints });
        // Start next segment from the same transition point
        curPhase = p.phase;
        curPoints = [p];
      } else {
        curPoints.push(p);
      }
    }
    if (curPoints.length > 0) segments.push({ phase: curPhase, points: curPoints });
    return segments;
  });

  function segmentPath(pts) {
    return pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
      .join(" ");
  }

  function segmentAreaPath(pts) {
    if (pts.length === 0) return "";
    const linePart = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
      .join(" ");
    const baselineY = PADDING.top + plotHeight;
    return (
      linePart +
      ` L ${xScale(pts[pts.length - 1].x)} ${baselineY}` +
      ` L ${xScale(pts[0].x)} ${baselineY}` +
      " Z"
    );
  }

  function phaseVar(phase) {
    if (phase === "study") return "--accent";
    if (phase === "gap") return "--warning";
    if (phase === "repay") {
      return outcome === "cleared" ? "--success" : "--coral";
    }
    return outcome === "cleared" ? "--accent" : "--coral";
  }

  // Y-axis ticks: 5 evenly spaced, rounded to nice numbers
  const yTicks = $derived(niceTicks(0, maxBalance, 5));

  // Default x-ticks (used only when caller didn't pass xTicks)
  const xStep = $derived(xRange > 25 ? 10 : 5);
  const defaultXTicks = $derived.by(() => {
    const t = [];
    for (let i = xStart; i <= xEnd; i += xStep) t.push(i);
    if (t[t.length - 1] !== xEnd) t.push(xEnd);
    return t;
  });
  const effectiveXTicks = $derived(xTicks ?? defaultXTicks);

  function niceTicks(min, max, count) {
    if (max <= 0) return [0];
    const rawStep = (max - min) / (count - 1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const norm = rawStep / magnitude;
    let niceStep;
    if (norm < 1.5) niceStep = 1;
    else if (norm < 3) niceStep = 2;
    else if (norm < 7) niceStep = 5;
    else niceStep = 10;
    niceStep *= magnitude;
    const ticks = [];
    for (let v = 0; v <= max * 1.05; v += niceStep) ticks.push(v);
    return ticks;
  }

  function fmtGBPShort(n) {
    if (n >= 1000) return "£" + (n / 1000).toFixed(0) + "k";
    return "£" + n.toFixed(0);
  }

  const lineColorVar = $derived(outcome === "cleared" ? "--accent" : "--coral");
</script>

<div class="chart-wrap">
<svg
  bind:this={svgEl}
  {width}
  {height}
  viewBox="0 0 {width} {height}"
  role="img"
  aria-label="Loan balance over time"
  preserveAspectRatio="xMidYMid meet"
  onpointermove={handlePointer}
  onpointerdown={handlePointer}
  onpointerleave={handleLeave}
>
  <!-- Y-axis grid + labels -->
  {#each yTicks as tick (tick)}
    <line
      x1={PADDING.left}
      x2={PADDING.left + plotWidth}
      y1={yScale(tick)}
      y2={yScale(tick)}
      class="grid"
    />
    <text
      x={PADDING.left - 8}
      y={yScale(tick)}
      class="axis-label"
      text-anchor="end"
      dominant-baseline="central"
    >
      {fmtGBPShort(tick)}
    </text>
  {/each}

  <!-- X-axis labels -->
  {#each effectiveXTicks as tick (tick)}
    <text
      x={xScale(tick)}
      y={PADDING.top + plotHeight + 20}
      class="axis-label"
      text-anchor="middle"
    >
      {xTickLabel ? xTickLabel(tick) : `Y${tick}`}
    </text>
  {/each}

  <!-- Area fill: coloured per phase in phased mode, single colour otherwise -->
  {#if phased && phaseSegments.length > 0}
    {#each phaseSegments as seg, i (i)}
      <path
        d={segmentAreaPath(seg.points)}
        class="area area-phased"
        style="fill: var({phaseVar(seg.phase)});"
      />
    {/each}
  {:else if areaPath}
    <path d={areaPath} class="area" style="fill: var({lineColorVar});" />
  {/if}

  <!-- Line: single colour, or per-phase segments -->
  {#if phased && phaseSegments.length > 0}
    {#each phaseSegments as seg, i (i)}
      <path
        d={segmentPath(seg.points)}
        class="line"
        style="stroke: var({phaseVar(seg.phase)});"
      />
    {/each}
  {:else if linePath}
    <path d={linePath} class="line" style="stroke: var({lineColorVar});" />
  {/if}

  <!-- Baseline -->
  <line
    x1={PADDING.left}
    x2={PADDING.left + plotWidth}
    y1={PADDING.top + plotHeight}
    y2={PADDING.top + plotHeight}
    class="baseline"
  />

  <!-- Hover indicator -->
  {#if hoveredRow != null}
    <line
      x1={hoverX}
      x2={hoverX}
      y1={PADDING.top}
      y2={PADDING.top + plotHeight}
      class="hover-line"
    />
    <circle cx={hoverX} cy={hoverY} r="5" class="hover-dot" />
  {/if}
</svg>

{#if hoveredRow != null}
  <div class="tooltip" style="left: {tooltipLeftPct}%;">
    <div class="tooltip-header">
      {#if phaseLabel(hoveredRow.phase)}
        {phaseLabel(hoveredRow.phase)} {hoveredRow.year}
      {:else}
        Year {hoveredRow.year}
      {/if}
    </div>
    <dl class="tooltip-rows">
      <div>
        <dt>Balance</dt>
        <dd class="tt-strong">{fmtGBPFull(hoveredRow.closingBalance)}</dd>
      </div>
      {#if hoveredRow.salary != null && hoveredRow.salary > 0}
        <div>
          <dt>Salary</dt>
          <dd>{fmtGBPFull(hoveredRow.salary)}</dd>
        </div>
      {/if}
      {#if hoveredRow.interestAdded != null}
        <div>
          <dt>Interest added</dt>
          <dd>
            {fmtGBPFull(hoveredRow.interestAdded)}
            {#if hoveredRow.interestRate != null}
              <span class="tt-hint">({fmtPctFull(hoveredRow.interestRate)})</span>
            {/if}
          </dd>
        </div>
      {/if}
      {#if hoveredRow.repayment != null && hoveredRow.repayment > 0}
        <div>
          <dt>Repayment</dt>
          <dd>{fmtGBPFull(hoveredRow.repayment)}</dd>
        </div>
      {/if}
    </dl>
  </div>
{/if}
</div>

<style>
  .chart-wrap { position: relative; }
  svg {
    display: block;
    width: 100%;
    height: auto;
    max-height: 320px;
    overflow: visible;
    touch-action: pan-y;
    cursor: crosshair;
  }
  .grid {
    stroke: var(--chart-grid);
    stroke-width: 1;
    stroke-dasharray: 2 3;
  }
  .baseline {
    stroke: var(--chart-axis);
    stroke-width: 1;
  }
  .axis-label {
    fill: var(--fg-muted);
    font-size: 11px;
    font-family: inherit;
    font-variant-numeric: tabular-nums;
  }
  .axis-label-sub {
    fill: var(--fg-subtle);
    font-size: 10px;
  }
  .area {
    fill-opacity: 0.12;
    stroke: none;
  }
  .area-phased {
    fill-opacity: 0.28;
  }
  .line {
    fill: none;
    stroke-width: 2.5;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  .hover-line {
    stroke: var(--fg-muted);
    stroke-width: 1;
    stroke-dasharray: 3 3;
    pointer-events: none;
  }
  .hover-dot {
    fill: var(--card);
    stroke: var(--fg);
    stroke-width: 2;
    pointer-events: none;
  }

  .tooltip {
    position: absolute;
    top: 4px;
    transform: translateX(-50%);
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.55rem;
    padding: 0.55rem 0.75rem;
    box-shadow: 0 8px 20px rgba(15, 61, 62, 0.12), 0 2px 4px rgba(15, 61, 62, 0.06);
    font-size: 0.82rem;
    color: var(--fg);
    pointer-events: none;
    min-width: 175px;
    max-width: 240px;
    z-index: 5;
  }
  .tooltip-header {
    font-weight: 700;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--accent);
    margin-bottom: 0.35rem;
  }
  .tooltip-rows { margin: 0; display: flex; flex-direction: column; gap: 0.15rem; }
  .tooltip-rows > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
  }
  .tooltip-rows dt {
    color: var(--fg-muted);
    font-size: 0.78rem;
    font-weight: 500;
  }
  .tooltip-rows dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
    font-weight: 600;
  }
  .tt-strong { color: var(--accent); font-weight: 800 !important; }
  .tt-hint {
    font-size: 0.75rem;
    color: var(--fg-subtle);
    font-weight: 500;
    margin-left: 0.15rem;
  }
</style>
